<template>
  <div class="collection-edit-create-container">
    <h1>{{ pageTitle }}</h1>

    <div v-if="isLoading" class="loading-state">
      <p>{{ t('collections.form.loading') }}</p>
      <div class="spinner"></div>
    </div>

    <div v-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <router-link to="/collections" class="btn btn-secondary">{{ t('collections.shared.backToList') }}</router-link>
    </div>

    <form v-if="!isLoading && !error" @submit.prevent="handleSubmit" class="collection-form">
      <div class="form-group">
        <label for="collectionName">{{ t('collections.form.fields.name.label') }}</label>
        <input type="text" id="collectionName" v-model.trim="collectionName" required />
        <p v-if="validationError.collectionName" class="validation-error">{{ validationError.collectionName }}</p>
      </div>

      <!-- Asset Search Section -->
      <div class="asset-search-section form-group">
        <label for="assetSearchQuery">{{ t('collections.form.fields.search.label') }}</label>
        <div class="search-input-group">
          <input
            type="text"
            id="assetSearchQuery"
            v-model="searchQuery"
            @input="onAssetSearchInput"
            :placeholder="t('collections.form.fields.search.placeholder')"
          />
          <button type="button" @click="handleSearchAssets" :disabled="isSearchingAssets" class="btn btn-info">
            {{ isSearchingAssets ? t('collections.form.fields.search.searching') : t('collections.form.fields.search.button') }}
          </button>
        </div>
        <p v-if="isSearchingAssets && searchQuery.trim()" class="loading-indicator search-loading">
          {{ t('collections.form.fields.search.loading') }}
        </p>
        <div v-if="searchError" class="error-message search-error">{{ searchError }}</div>
        <ul v-if="searchResults.length > 0" class="search-results-list">
          <li v-for="asset in searchResults" :key="asset.code">
            <span>{{ asset.name }} ({{ asset.code }})</span>
            <button
              type="button"
              @click="addAssetToCollection(asset)"
              :disabled="isAssetInCurrentCollection(asset)"
              class="btn btn-sm btn-success"
            >
              {{ isAssetInCurrentCollection(asset) ? t('collections.form.actions.added') : t('collections.form.actions.add') }}
            </button>
          </li>
        </ul>
        <p v-if="showNoResultsMessage">{{ t('collections.form.results.noMatches') }}</p>
      </div>

      <!-- Current Assets Section -->
      <div class="current-assets-section form-group">
        <h3>{{ t('collections.form.currentAssets.heading', { count: currentAssets.length }) }}</h3>
        <ul v-if="currentAssets.length > 0" class="current-assets-list">
          <li v-for="asset in currentAssets" :key="asset.code">
            <span>{{ asset.name }} ({{ asset.code }})</span>
            <button type="button" @click="removeAssetFromCurrentList(asset.code)" class="btn btn-sm btn-danger">
              {{ t('collections.shared.actions.remove') }}
            </button>
          </li>
        </ul>
        <p v-else class="empty-assets-message">{{ t('collections.form.currentAssets.empty') }}</p>
      </div>

      <div class="form-actions">
        <button type="submit" :disabled="isSaving" class="btn btn-primary btn-submit">
          {{ submitButtonText }}
        </button>
        <router-link to="/collections" class="btn btn-link">{{ t('common.cancel') }}</router-link>
      </div>
       <p v-if="isSaving" class="saving-indicator">{{ t('collections.form.saving') }}</p>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'; // Removed 'watch' as it's commented out
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
    getCollectionDetails as apiGetCollectionDetails,
    searchAssets as apiSearchAssets,
    createCollection as apiCreateCollection,
    updateCollection as apiUpdateCollection
} from '@/services/api.js';

/**
 * @file CollectionEditCreatePage.vue
 * @description A Vue component page that serves for both creating new asset collections
 * and editing existing ones. The behavior adapts based on the presence of a collection ID
 * in the route parameters.
 */

const route = useRoute();
const router = useRouter();

/**
 * Reactive ref for the collection ID from route parameters. Null if in create mode.
 * @type {import('vue').Ref<string|null>}
 */
const collectionId = ref(route.params.id || null);

/**
 * Computed property to determine if the page is in "edit" mode (true) or "create" mode (false).
 * @type {import('vue').ComputedRef<boolean>}
 */
const isEditMode = computed(() => !!collectionId.value);

/**
 * Reactive ref to store the initial name of the collection when in edit mode.
 * Used for display purposes in the page title while editing.
 * @type {import('vue').Ref<string>}
 */
const initialCollectionName = ref('');

// --- Form Data ---
/**
 * Reactive ref for the collection's name input field.
 * @type {import('vue').Ref<string>}
 */
const collectionName = ref('');
/**
 * Reactive array holding the assets currently part of the collection being edited/created.
 * Each element is an object, e.g., `{ code: 'AAPL', name: 'Apple Inc.' }`.
 * @type {import('vue').Ref<Array<object>>}
 */
const currentAssets = ref([]);

// --- Asset Search State ---
/**
 * Reactive ref for the asset search input query.
 * @type {import('vue').Ref<string>}
 */
const searchQuery = ref('');
/**
 * Reactive array to store results from asset search.
 * @type {import('vue').Ref<Array<object>>}
 */
const searchResults = ref([]);
/**
 * Reactive boolean indicating if an asset search is currently in progress.
 * @type {import('vue').Ref<boolean>}
 */
const isSearchingAssets = ref(false);
/**
 * Reactive ref for storing error messages related to asset search.
 * @type {import('vue').Ref<string|null>}
 */
const searchError = ref(null);
/**
 * Reactive boolean to control the display of a "no results found" message for asset search.
 * @type {import('vue').Ref<boolean>}
 */
const showNoResultsMessage = ref(false);
/**
 * Reactive ref to store the timeout ID for debouncing asset search input.
 * @type {import('vue').Ref<number|null>}
 */
const searchDebounceTimeout = ref(null);

// --- Component General State ---
/**
 * Reactive boolean indicating if initial collection data is loading (in edit mode).
 * @type {import('vue').Ref<boolean>}
 */
const isLoading = ref(false);
/**
 * Reactive boolean indicating if a save (create/update) operation is in progress.
 * @type {import('vue').Ref<boolean>}
 */
const isSaving = ref(false);
/**
 * Reactive ref for storing general page-level or form submission error messages.
 * @type {import('vue').Ref<string|null>}
 */
const error = ref(null);
/**
 * Reactive object for storing field-specific validation errors.
 * @type {import('vue').Ref<object>}
 * @example { collectionName: "Name is too short." }
 */
const validationError = ref({});
const { t } = useI18n();

/**
 * Computed property for the page title, changes based on edit/create mode.
 * @type {import('vue').ComputedRef<string>}
 */
const pageTitle = computed(() => {
  if (isEditMode.value) {
    const name = initialCollectionName.value || t('collections.form.loadingName');
    return t('collections.form.titleEdit', { name });
  }
  return t('collections.form.titleCreate');
});
/**
 * Computed property for the submit button text, changes based on edit/create mode.
 * @type {import('vue').ComputedRef<string>}
 */
const submitButtonText = computed(() =>
  isEditMode.value ? t('collections.form.buttons.update') : t('collections.form.buttons.save')
);

/**
 * Handles debounced input for the asset search field.
 * Clears any existing debounce timeout and sets a new one.
 * If the search query becomes empty after debounce, it clears results and messages.
 * Otherwise, it calls `handleSearchAssets` to perform the search.
 */
const onAssetSearchInput = () => {
  if (searchDebounceTimeout.value) {
    clearTimeout(searchDebounceTimeout.value);
  }
  searchDebounceTimeout.value = setTimeout(() => {
    // Do not search if query is empty, instead clear results and messages
    if (!searchQuery.value.trim()) {
        searchResults.value = [];
        searchError.value = null; // Clear previous search errors
        showNoResultsMessage.value = false; // Hide no results message
        return; // Exit if query is empty after debounce
    }
    handleSearchAssets();
  }, 500); // 500ms debounce delay
};

/**
 * Fetches collection data from the API when the component is in edit mode.
 * Populates `collectionName`, `initialCollectionName`, and `currentAssets` refs.
 * Handles loading and error states.
 * @async
 * @param {string} id - The ID of the collection to fetch.
 */
const fetchCollectionData = async (id) => {
  isLoading.value = true;
  error.value = null;
  try {
    const data = await apiGetCollectionDetails(id);
    collectionName.value = data.name;
    initialCollectionName.value = data.name; // Store for title
    // Ensure assets are in the format { code, name }
    currentAssets.value = data.assets.map(asset => ({
        code: asset.code,
        name: asset.name || t('collections.shared.unknownAsset') // Fallback if name isn't provided by getCollectionDetails
    }));
  } catch (err) {
    console.error('Failed to fetch collection details:', err);
    const reason = err?.message || t('common.unknownError');
    error.value = t('collections.form.errors.loadFailed', { reason });
  } finally {
    isLoading.value = false;
  }
};

/**
 * Performs an asynchronous search for assets based on `searchQuery`.
 * Updates `searchResults`, `searchError`, and `showNoResultsMessage` reactive properties.
 * Manages the `isSearchingAssets` loading state.
 * @async
 */
const handleSearchAssets = async () => {
  if (!searchQuery.value.trim()) {
    searchResults.value = [];
    searchError.value = t('collections.form.errors.emptySearch');
    showNoResultsMessage.value = false;
    return;
  }
  isSearchingAssets.value = true;
  searchError.value = null;
  showNoResultsMessage.value = false;
  try {
    const results = await apiSearchAssets(searchQuery.value);
    searchResults.value = results;
    if (results.length === 0) {
        showNoResultsMessage.value = true;
    }
  } catch (err) {
    console.error('Failed to search assets:', err);
    const reason = err?.message || t('common.unknownError');
    searchError.value = t('collections.form.errors.searchFailed', { reason });
    searchResults.value = [];
  } finally {
    isSearchingAssets.value = false;
  }
};

/**
 * Adds a selected asset object from search results to the `currentAssets` list.
 * Prevents duplicate assets (by code) from being added.
 * Clears search query and results after adding an asset.
 * @param {object} asset - The asset object to add (should contain `code` and `name`).
 */
const addAssetToCollection = (asset) => {
  if (!currentAssets.value.find(a => a.code === asset.code)) {
    currentAssets.value.push(asset);
  }
  // searchResults.value = []; // Keep search results
  // searchResults.value = []; // Keep search results
  // searchQuery.value = '';   // Keep search query
  // showNoResultsMessage.value = false; // Not needed if results are kept
};

/**
 * Checks if a given asset (typically from search results) is already in the `currentAssets` list.
 * @param {object} asset - The asset object to check (must have a `code` property).
 * @returns {boolean} True if the asset is in `currentAssets`, false otherwise.
 */
const isAssetInCurrentCollection = (asset) => {
  return currentAssets.value.some(a => a.code === asset.code);
};

/**
 * Removes an asset from the `currentAssets` list based on its code.
 * @param {string} assetCode - The code of the asset to be removed.
 */
const removeAssetFromCurrentList = (assetCode) => {
  currentAssets.value = currentAssets.value.filter(a => a.code !== assetCode);
};

/**
 * Validates the collection form data, primarily the `collectionName`.
 * Updates the `validationError` reactive property with any error messages.
 * @returns {boolean} `true` if the form is valid, `false` otherwise.
 */
const validateForm = () => {
    validationError.value = {};
    let isValid = true;
    if (!collectionName.value || collectionName.value.length < 3) {
        validationError.value.collectionName = t('collections.form.errors.nameTooShort');
        isValid = false;
    }
    // Add more validation if needed (e.g., at least one asset)
    return isValid;
};

/**
 * Handles the submission of the collection form.
 * Validates the form, then either calls the API to create a new collection or update
 * an existing one, based on `isEditMode`. Handles API responses, updates loading/error states,
 * and redirects on success.
 * @async
 */
const handleSubmit = async () => {
  if (!validateForm()) return;

  isSaving.value = true;
  error.value = null;

  const collectionPayload = {
    name: collectionName.value,
    assets: currentAssets.value.map(a => a.code), // Submit only asset codes
  };

  try {
    let response;
    if (isEditMode.value) {
      response = await apiUpdateCollection(collectionId.value, collectionPayload);
      // Assuming apiUpdateCollection also returns the updated object or similar
      // and fetchWithAuth handles non-2xx as errors.
      // If apiUpdateCollection might return a specific success flag, adjust here too.
      if (response && response.id) { // Check if response is valid (e.g., has an id)
        alert(t('collections.form.messages.updateSuccess'));
        router.push(`/collections/${collectionId.value}`);
      } else {
        // This else might not be hit if fetchWithAuth throws on non-OK responses.
        // Handling it defensively or relying on the catch block.
        throw new Error(response?.message || t('collections.form.errors.updateFailed'));
      }
    } else { // Create mode
      response = await apiCreateCollection(collectionPayload);
      // For create, a 201 response body is typically the created object.
      // Check if the response object looks like a successfully created collection (e.g., has an id).
      if (response && response.id) {
        alert(t('collections.form.messages.createSuccess'));
        router.push(response.id ? `/collections/${response.id}` : '/collections');
      } else {
        // This else might indicate an unexpected response format from a 2xx status,
        // or it could be that fetchWithAuth already threw an error for non-2xx.
        // If fetchWithAuth guarantees an error for non-2xx, this specific else might be redundant.
        throw new Error(response?.message || t('collections.form.errors.createFailed'));
      }
    }
  } catch (err) {
    // Catches errors from API calls (e.g., network issues, or thrown by new Error above from fetchWithAuth)
    console.error('Failed to save collection:', err);
    const reason = err?.message || t('common.unknownError');
    error.value = t('collections.form.errors.saveFailed', { reason });
  } finally {
    isSaving.value = false;
  }
};

// If in edit mode, fetches the initial collection data when the component is mounted.
onMounted(() => {
  if (isEditMode.value) {
    fetchCollectionData(collectionId.value);
  }
});

// Optional: Watch for route parameter changes if the same component instance could be reused
// for navigating between creating a new collection and editing one, or editing different collections.
// This is less common for pages but can happen with dynamic routing.
// import { watch } from 'vue';
// watch(() => route.params.id, (newId) => {
//   collectionId.value = newId || null;
//   initialCollectionName.value = ''; // Reset
//   collectionName.value = '';
//   currentAssets.value = [];
//   error.value = null;
//   validationError.value = {};
//   if (isEditMode.value) { // isEditMode will recompute based on newId
//     fetchCollectionData(newId);
//   }
// });
</script>

<style scoped>
.collection-edit-create-container {
  max-width: 700px;
  margin: 20px auto;
  padding: 25px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  font-family: Arial, sans-serif;
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 25px;
}

.collection-form .form-group {
  margin-bottom: 20px;
}

.collection-form label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #555;
}

.collection-form input[type="text"] {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}
.collection-form input[type="text"]:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.validation-error {
    color: #dc3545;
    font-size: 0.85em;
    margin-top: 4px;
}

.search-input-group {
  display: flex;
  gap: 10px;
}
.search-input-group input[type="text"] {
  flex-grow: 1;
}

.search-results-list, .current-assets-list {
  list-style-type: none;
  padding-left: 0;
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 4px;
}

.search-results-list li, .current-assets-list li {
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
}
.search-results-list li:last-child, .current-assets-list li:last-child {
  border-bottom: none;
}

.search-error, .empty-assets-message, .showNoResultsMessage, .saving-indicator {
    font-size: 0.9em;
    color: #555;
    margin-top: 8px;
}
.search-error { color: #dc3545; }
.saving-indicator { color: #007bff; font-weight: bold; }


.current-assets-section h3 {
  font-size: 1.2em;
  color: #444;
  margin-bottom: 10px;
  border-bottom: 1px solid #eee;
  padding-bottom: 5px;
}

.form-actions {
  margin-top: 30px;
  text-align: right;
}
.form-actions .btn-link {
    margin-left: 10px;
    color: #007bff;
    text-decoration: none;
}
.form-actions .btn-link:hover {
    text-decoration: underline;
}


/* Basic button styling (reuse from other components if global styles exist) */
.btn {
  padding: 8px 15px;
  border-radius: 4px;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.9em;
  border: 1px solid transparent;
}
.btn-primary { background-color: #007bff; color: white; border-color: #007bff; }
.btn-primary:hover { background-color: #0056b3; }
.btn-info { background-color: #17a2b8; color: white; }
.btn-success { background-color: #28a745; color: white; }
.btn-danger { background-color: #dc3545; color: white; }
.btn-sm { padding: 5px 10px; font-size: 0.8em; }

.btn:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    opacity: 0.65;
}
.btn-submit:disabled { /* Keep primary color but indicate disabled */
    background-color: #007bff;
    opacity: 0.65;
}


.loading-state, .error-state {
  text-align: center;
  padding: 40px 0;
  color: #555;
}
.error-message { color: #dc3545; }
.spinner {
  border: 4px solid rgba(0,0,0,0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #007bff;
  animation: spin 1s ease infinite;
  margin: 20px auto;
}
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>
